import { GtagEvent } from './gx-gtag/gtag-interfaces';
import { deburr, isEqual, pickBy } from 'lodash';
import { CampaignsChildRoutes, MainRoutes, UserPermission } from './enums';
import { AbstractControl, UntypedFormArray, UntypedFormControl, ValidatorFn } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Account, AiInsightsMode } from '../models/account';
import { UserDetails } from '../models/users-models/user-details';

export function refreshPage() {
  window.location.reload();
}

export function isStringAnUrl(str: string): boolean {
  const regex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  return regex.test(str);
}

/**
 * Cleans string from whitespaces, converts it to lowercase and converts all special letters to basic Latin letters (e.g. 'Genève ' -> 'geneve').
 * @param text String to clean
 */
export function cleanStringForComparison(text: string): string {
  return deburr(text.trim().toLowerCase());
}

/**
 * Converts a JSON object that represents a map to a Typescript Map
 * @param jsonMap JSON object that represents a map
 */
export function convertJsonToMap(jsonMap): Map<any, any> {
  let newMap = new Map();
  for (let key of Object.keys(jsonMap)) {
    newMap.set(key, jsonMap[key]);
  }
  return newMap;
}

/**
 * Converts a Typescript Map to a JSON object that represents a map
 * @param map Typescript Map
 */
export function convertMapToJson(map: Map<any, any>): Object {
  if (!map || map.size === 0) {
    return null;
  }
  let jsonObject = {};
  map.forEach((value, key) => {
    jsonObject[key] = value;
  });
  return jsonObject;
}

/**
 * Returns a list of enum values.
 * @param e The enum in question
 */
export function getEnumValues<EnumType = any>(e: EnumType): Array<EnumType[keyof EnumType]> {
  let arr = [];
  for (const enumMember in e) {
    arr.push(e[enumMember]);
  }
  return arr;
}

/**
 * Gets a shallow difference between two objects, returning an object
 * containing only the properties that are different in the source object (source)
 * compared to the base object (base).
 * @param source The object whose changed values you want to see.
 * @param base The object to compare against (the original state).
 * @returns An object with only the properties that differ.
 */
export const getObjectDifference = (source: Dictionary<any>, base: Dictionary<any>) => {
  // 1. Iterate over the keys of the source object (e.g., objB)
  return pickBy(source, (sourceValue, key) => {
    // 2. Check if the value in objA is NOT deep equal to the value in objB
    return !isEqual(sourceValue, base[key]);
  });
};

/**
 * Useful for query params normalization. Will check against validValues and always returns an
 * array. (resp. query param will be of type 'string' if a listing filter is just 1 item).
 * @param value
 * @param validValues
 * @private
 */
export function normalizeToArray(value: string | string[], validValues: string[]) {
  if (value instanceof Array && value.every(item => validValues.includes(item))) {
    return value;
  } else if (typeof value === 'string' && validValues.includes(value)) {
    return [value];
  } else return [];
}

/**
 * Sends an event (e.g. button click) to Google Analytics.
 * @param category Categorizes the user activity (required)
 * @param action What the user does (required)
 * @param label
 */
export function sendToGoogleAnalytics(category: string, action: string, label?: string) {

  // BLS-4425: differentiating bet. the "old" and the overhauled address list
  const isOverhaul = window.location.pathname.includes('/locations');
  category = isOverhaul ? `${category} [overhaul]` : category;

  const params: GtagEvent = {
    event_category: category,
  };

  if (label) {
    params.event_label = label;
  }

  (window as any).gtag('event', action, params);
}

/**
 * Returns the styling for clusters on the Google Map.
 */
export function getClusterStyles() {
  return [
    {
      textColor: 'white',
      textSize: 15,
      url: 'assets/images/mapMarkers/m/m1.png',
      height: 60,
      width: 60,
    },
    {
      textColor: 'white',
      textSize: 15,
      url: 'assets/images/mapMarkers/m/m2.png',
      height: 70,
      width: 70,
    },
    {
      textColor: 'white',
      textSize: 15,
      url: 'assets/images/mapMarkers/m/m3.png',
      height: 80,
      width: 80,
    },
    {
      textColor: 'white',
      textSize: 15,
      url: 'assets/images/mapMarkers/m/m4.png',
      height: 90,
      width: 90,
    },
    {
      textColor: 'white',
      textSize: 15,
      url: 'assets/images/mapMarkers/m/m5.png',
      height: 100,
      width: 100,
    },
  ];
}

/**
 * Checks if today's date is between December 1st and January 6th (inclusive).
 */
export function isChristmasSeason(): boolean {
  const today = new Date();
  if (today.getMonth() === 11) {
    //if it's December
    return true;
  } else if (today.getMonth() === 0 && today.getDate() < 7) {
    //until January 6th (Heilige 3 Könige)
    return true;
  }
  return false;
}

/**
 * Returns a timezone time zone value regarding https://cloud.google.com/dataprep/docs/html/Supported-Time-Zone-Values_66194188
 * @returns {string}
 */
export function getClientTimezoneValue(): string {
  let timeZoneValue = null;
  try {
    timeZoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    console.log('Time Zone Value not defined. Use default.');
  }
  return timeZoneValue;
}

/**
 * Given an array item index, return the corresponding alpha letter
 * of the form A, B, C, ... AA, AB, AC ... ZZZ ...
 * @param arrayIndexOfInterest
 * @returns {string}
 */
export function getAlphabetNumberingSchemeLetter(arrayIndexOfInterest: number): string {
  // edge case
  if (arrayIndexOfInterest < 0) {
    return 'A';
  }

  // the function below is adapted from https://codereview.stackexchange.com/a/16129/225800
  // but starts returning "correct" letters only after index 1 -- so we'll increment
  // `arrayIndexOfInterest` by one to align the stars

  arrayIndexOfInterest++;

  const baseChar = ('A').charCodeAt(0);
  let letters = '';

  do {
    arrayIndexOfInterest -= 1;
    letters = String.fromCharCode(baseChar + (arrayIndexOfInterest % 26)) + letters;
    arrayIndexOfInterest = (arrayIndexOfInterest / 26) >> 0; // quick `floor`
  } while (arrayIndexOfInterest > 0);

  return letters;
}


/**
 * Given an `svgString` like `<svg>abc</svg>`, return it's URI-encoded representation
 * to be (primarily) used as a map marker icon.
 * @param {string} svgString
 */
export function encodeSVG(svgString: string): string {
  const symbols = /[\r\n%#()<>?\[\\\]^`{|}]/g;

  // Use single quotes instead of double to avoid URI encoding
  svgString = svgString.replace(/'/g, '"');
  svgString = svgString.replace(/>\s+</g, '><');
  svgString = svgString.replace(/\s{2,}/g, ' ');

  return 'data:image/svg+xml;utf-8,' + svgString.replace(symbols, encodeURIComponent);
}

/**
 * Check whether the mouse is "above" an HTML element
 * @param mousePosition
 * @param element Element to check.
 * Adapted from https://stackoverflow.com/a/54000580/8160318
 */
export function isMouseOverElement(
    mousePosition: { x: number; y: number },
    element: Element | HTMLElement | undefined,
) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const { x, y } = mousePosition;

  if (x < rect.left || x >= rect.right) return false;
  if (y < rect.top || y >= rect.bottom) return false;
  return true;
}

/**
 * Returns the hexcode of a css-color-variable (defined in _colorDefinitions.scss). If the variable
 * cannot be found, hexcode for black is returned.
 * @param colorName Name of the color-variable without leading '--' (e.g. 'blue-40')
 */
export function getHexCode(colorName: string): string {
  const hexCode = getComputedStyle(document.body).getPropertyValue('--' + colorName);
  return hexCode ? hexCode : '#000000';
}

/**
 * Capitalizes the first letter of an input string.
 * https://stackoverflow.com/a/7224605/8160318
 * @param str
 */
export const capitalizeFirstLetter = (str: string): string => {
  return str && str[0].toUpperCase() + str.slice(1);
};

/**
 * Sets a key-value pair to the browser's local storage. An optional time-to-live can be set.
 * @param key Name of the localStorage-item.
 * @param value Value of the localStorage-item (not stringified).
 * @param expirationTime Time (in milliseconds) when the localStorage-item expires and returns null again on getItem().
 */
export const setToLocalStorage = (key: string, value: any, expirationTime?: number) => {
  const item = expirationTime ? { value: value, expiry: expirationTime } : value;
  localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Gets the value of the localStorage-item with the specified name. Also checks the expiration
 * time and returns null in case the item expired.
 * @param key Name of the localStorage-item.
 * @returns {Object, Array, string, number, boolean, or null} value corresponding to the given
 * JSON text
 */
export const getFromLocalStorage = (key: string): any => {
  const itemStr: string = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }
  try {
    const item: any = JSON.parse(itemStr);
    if (!item?.expiry) {
      return item;
    }
    // check if the localStorage-item has expired
    if (new Date().getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (e) {
    return itemStr;
  }
};

/**
 * Returns the base redirect uri after user logs in
 * This uri needs to be whitelisted as the callback/logout/allowed web origins uri in auth0
 * app dashboard
 * If the user is signed in, they will be redirected to redirectUri/home
 * (as specified in the Routes config in app.module)
 * If the user is not signed in, they will be automatically routed to the universal auth0 login page
 * since none of our routes are currently allowed to be viewed by unknown users (this is done
 * presumably by the auth0 authGuard)
 */
export const getAuth0RedirectUri = (): string => {
  return document.location.origin + '/';
};

/**
 * Checks if the path is among user permissions. Implemented by PermissionsGuard and NavigationComponent.
 * Each path that we want Angular to guard with user permissions has to be set here manually
 * and make sure PermissionsGuard is set in appRoutes config for the respective path
 * @param userDetails
 * @param path
 * @param acc
 */
export const hasViewPermissionForPath = (
    userDetails: UserDetails | undefined, path: string, acc: Account): boolean => {
  const userPermissions = userDetails?.permissions;
  switch (path) {
    case MainRoutes.LOCATIONS:
      return userPermissions?.includes(UserPermission.viewLocations);
    case MainRoutes.USERS:
      return userPermissions?.includes(UserPermission.viewUsers);
    case MainRoutes.REVIEWS:
      return userPermissions?.includes(UserPermission.viewReviews);
    case MainRoutes.INSIGHTS:
      return userPermissions?.includes(UserPermission.viewInsights);
    case MainRoutes.AI_INSIGHTS:
      return userPermissions?.includes(UserPermission.viewInsights) &&
          acc?.aiInsightsMode === AiInsightsMode.ENABLED ||
          acc?.aiInsightsMode === AiInsightsMode.PREVIEW;
    case MainRoutes.POSTS:
      return userPermissions?.includes(UserPermission.viewCampaigns);
    case CampaignsChildRoutes.PLATFORMS:
      return userPermissions?.includes(UserPermission.viewCampaigns) &&
          !!userDetails?.userFeatures?.socialMediaActive;
    default:
      return false;
  }

};

/**
 * A FormArray validator to be used with an array of checkboxes where at least 1 should be checked
 */
export const atLeastOneCheckboxCheckedValidator = (): ValidatorFn => {
  return function validate(formArray: UntypedFormArray) {
    let checked = 0;

    Object.keys(formArray.controls).forEach(key => {
      const control = formArray.controls[key];

      if (control.value === true) {
        checked++;
      }
    });

    if (checked < 1) {
      return {
        // error code = 'requireCheckboxToBeChecked'
        requireCheckboxToBeChecked: true,
      };
    }

    return null;
  };
};

/**
 * Annoyingly, the formControl directive expects FormControl, but FormBuilder returns AbstractControl,
 * hence this utility function
 * @param control
 */
export const castAsFormControl = (control: AbstractControl): UntypedFormControl => {
  return control as UntypedFormControl;
};

/**
 * Checks if it's possible to show current page given a page size and total amount of items.
 * If it is, will return unmodified page index.
 * If it's not, will recursively reduce page index until a sensible one is found.
 * @param total
 * @param pageSize
 * @param pageIndex
 */
export function getNewPageIndex(total: number, pageSize: number, pageIndex: number): number {
  if (pageSize <= 0 || total <= 0 || pageIndex <= 0) {
    return 0;
  }

  // Calculate the maximum possible pageIndex based on pageSize and reviewTotal
  const maxPageIndex = Math.floor((total - 1) / pageSize);

  if (pageIndex <= maxPageIndex) {
    return pageIndex;
  } else {
    getNewPageIndex(total, pageSize, pageIndex - 1);
  }
}

export function isNumber(value?: string | number): boolean {
  return ((value != null) &&
      (value !== '') &&
      !isNaN(Number(value.toString())));
}

export function isValidCountryISOCode(displayNames: Intl.DisplayNames, countryCode: string) {
  let isValidCountryCode = false;

  try {
    displayNames.of(countryCode);
    isValidCountryCode = true;
  } catch (e) {
    console.warn(e.name, e.message);
  }

  return isValidCountryCode;
}
